import { ArrowLeft, ChevronRight, Loader2, Puzzle, Search } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { MailAccountsPanel } from "@/components/mail-accounts-panel"
import { NotionPanel } from "@/components/notion-panel"
import { TelegramPanel } from "@/components/telegram-panel"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import { beginPluginOAuth } from "@/lib/plugin-oauth"
import type { AgentPlugin, Plugin } from "@/lib/types"

/** Plugins whose settings need more than the manifest's flat config fields. */
const pluginPanels: Record<
  string,
  { badgeKey?: string; Panel: React.ComponentType<{ agentId: string }> }
> = {
  mail: { badgeKey: "pluginsTab.mailAccountSetupBadge", Panel: MailAccountsPanel },
  notion: { badgeKey: "pluginsTab.notionSetupBadge", Panel: NotionPanel },
  telegram: { badgeKey: "pluginsTab.telegramSetupBadge", Panel: TelegramPanel },
}

/** Browse-and-detail plugin management for one agent (the agent editor's Plugins tab). */
export function PluginsTab({ agentId, initialSlug }: { agentId: string; initialSlug?: string }) {
  const t = useT()
  const [loading, setLoading] = React.useState(true)
  const [plugins, setPlugins] = React.useState<Plugin[]>([])
  const [installs, setInstalls] = React.useState<Record<string, AgentPlugin>>({})
  const [selectedSlug, setSelectedSlug] = React.useState<string | null>(initialSlug ?? null)
  const [query, setQuery] = React.useState("")

  const load = React.useCallback(async () => {
    try {
      const [pluginList, installList] = await Promise.all([
        api.listPlugins(),
        api.agentPlugins(agentId),
      ])
      setPlugins(pluginList)
      const index: Record<string, AgentPlugin> = {}
      for (const install of installList) index[install.plugin.slug] = install
      setInstalls(index)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("pluginsTab.toasts.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [agentId, t])

  React.useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return <div className="text-muted-foreground p-1 text-sm">{t("pluginsTab.loading")}</div>
  }

  // Catalog plugins first; a removed plugin the agent still has installed stays visible
  // (it is excluded from the catalog itself) so it can be inspected and disabled.
  const rows = [...plugins]
  for (const install of Object.values(installs)) {
    if (!plugins.some((plugin) => plugin.slug === install.plugin.slug)) {
      rows.push(install.plugin)
    }
  }

  const selected = selectedSlug ? rows.find((plugin) => plugin.slug === selectedSlug) : undefined
  if (selected) {
    return (
      <PluginDetail
        agentId={agentId}
        plugin={selected}
        install={installs[selected.slug]}
        onBack={() => setSelectedSlug(null)}
        onChange={load}
      />
    )
  }

  const needle = query.trim().toLowerCase()
  const visible = needle
    ? rows.filter((plugin) =>
        [plugin.name, plugin.slug, plugin.description]
          .join(" ")
          .toLowerCase()
          .includes(needle),
      )
    : rows

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Puzzle className="size-4" />
          {t("pluginsTab.browseTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("pluginsTab.searchPlaceholder")}
            className="pl-9"
          />
        </div>
        {visible.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {rows.length === 0 ? t("pluginsTab.emptyNone") : t("pluginsTab.emptyNoMatch", { query })}
          </p>
        ) : (
          visible.map((plugin) => {
            const install = installs[plugin.slug]
            const needsKey = plugin.external_servers.some((server) =>
              server.config.some((field) => field.required && field.secret),
            )
            return (
              <button
                key={plugin.slug}
                type="button"
                onClick={() => setSelectedSlug(plugin.slug)}
                className="hover:bg-muted/50 flex w-full items-center justify-between gap-3 rounded-lg border p-4 text-left"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2 font-medium">
                    {plugin.name}
                    <Badge variant="outline">v{plugin.version}</Badge>
                    {install?.enabled && <Badge>{t("common.enabled")}</Badge>}
                    {needsKey && <Badge variant="secondary">{t("pluginsTab.needsApiKeyBadge")}</Badge>}
                    {plugin.oauth && (
                      <Badge variant="secondary">{t("pluginsTab.needsGoogleBadge")}</Badge>
                    )}
                    {pluginPanels[plugin.slug]?.badgeKey && (
                      <Badge variant="secondary">{t(pluginPanels[plugin.slug].badgeKey!)}</Badge>
                    )}
                    {plugin.removed && <Badge variant="secondary">{t("pluginsTab.removedBadge")}</Badge>}
                  </div>
                  <p className="text-muted-foreground text-xs">{plugin.description}</p>
                </div>
                <ChevronRight className="text-muted-foreground size-4 shrink-0" />
              </button>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}

function PluginDetail({
  agentId,
  plugin,
  install,
  onBack,
  onChange,
}: {
  agentId: string
  plugin: Plugin
  install: AgentPlugin | undefined
  onBack: () => void
  onChange: () => Promise<void>
}) {
  const t = useT()
  const enabled = install?.enabled ?? false
  const removed = plugin.removed
  const hasConfigFields = plugin.external_servers.some((server) => server.config.length > 0)

  // True while the config panel is open for a not-yet-saved enable, so the switch can
  // show "on" and be clicked again to cancel without an API call.
  const [pendingOn, setPendingOn] = React.useState(false)
  const [values, setValues] = React.useState<Record<string, Record<string, string>>>({})
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (enabled) setPendingOn(false)
  }, [enabled])

  function setFieldValue(serverSlug: string, key: string, value: string) {
    setValues((current) => ({
      ...current,
      [serverSlug]: { ...current[serverSlug], [key]: value },
    }))
  }

  async function enableWithoutConfig() {
    setBusy(true)
    try {
      await api.saveAgentPlugin(agentId, plugin.slug, { enabled: true, config: {} })
      toast.success(t("pluginsTab.toasts.enabled", { name: plugin.name }))
      await onChange()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("pluginsTab.toasts.enableFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function saveConfig() {
    const config: Record<string, Record<string, string>> = {}
    for (const server of plugin.external_servers) {
      const serverValues = values[server.slug]
      if (!serverValues) continue
      const nonBlank = Object.fromEntries(
        Object.entries(serverValues).filter(([, value]) => value.trim() !== ""),
      )
      if (Object.keys(nonBlank).length > 0) config[server.slug] = nonBlank
    }

    const missing: string[] = []
    for (const server of plugin.external_servers) {
      const state = install?.servers[server.slug]
      for (const field of server.config) {
        if (!field.required) continue
        const alreadySet = state ? field.key in state.set_fields : false
        const provided = (values[server.slug]?.[field.key] ?? "").trim() !== ""
        if (!alreadySet && !provided) missing.push(field.label)
      }
    }
    if (missing.length > 0) {
      toast.error(t("pluginsTab.toasts.missingRequired", { fields: missing.join(", ") }))
      return
    }

    setBusy(true)
    try {
      await api.saveAgentPlugin(agentId, plugin.slug, { enabled: true, config })
      toast.success(t("pluginsTab.toasts.saved", { name: plugin.name }))
      setValues({})
      setPendingOn(false)
      await onChange()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("pluginsTab.toasts.saveConfigFailed"))
    } finally {
      setBusy(false)
    }
  }

  async function disable() {
    const prompt = plugin.oauth
      ? t("pluginsTab.disableConfirmOAuth")
      : t("pluginsTab.disableConfirmPlain")
    if (!confirm(prompt)) return
    setBusy(true)
    try {
      await api.deleteAgentPlugin(agentId, plugin.slug)
      toast.success(t("pluginsTab.toasts.disabled", { name: plugin.name }))
      setValues({})
      await onChange()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("pluginsTab.toasts.disableFailed"))
    } finally {
      setBusy(false)
    }
  }

  function handleToggle(checked: boolean) {
    if (checked) {
      if (enabled) return
      if (hasConfigFields) {
        setPendingOn(true)
        return
      }
      void enableWithoutConfig()
      return
    }
    if (pendingOn) {
      // Cancel the not-yet-saved enable; nothing was persisted.
      setPendingOn(false)
      setValues({})
      return
    }
    void disable()
  }

  const showConfig = hasConfigFields && (enabled || pendingOn) && !removed
  const ExtraPanel = pluginPanels[plugin.slug]?.Panel

  return (
    <div className="space-y-4">
      <Button type="button" variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="size-4" />
        {t("pluginsTab.backButton")}
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex flex-wrap items-center gap-2">
                {plugin.name}
                <Badge variant="outline">v{plugin.version}</Badge>
                {removed && <Badge variant="secondary">{t("pluginsTab.removedBadge")}</Badge>}
              </CardTitle>
              <p className="text-muted-foreground mt-1 font-mono text-xs">{plugin.slug}</p>
              <p className="text-muted-foreground mt-2 text-sm">{plugin.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="plugin-enabled" className="text-xs font-normal">
                {t("common.enabled")}
              </Label>
              <Switch
                id="plugin-enabled"
                checked={enabled || pendingOn}
                disabled={busy || (removed && !enabled)}
                onCheckedChange={handleToggle}
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {plugin.oauth && enabled && !removed && (
        <OAuthCard agentId={agentId} plugin={plugin} install={install} onChange={onChange} />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("pluginsTab.skillsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {plugin.skills.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("pluginsTab.noSkills")}</p>
          ) : (
            plugin.skills.map((skill) => (
              <div key={skill.name} className="rounded-lg border p-3">
                <p className="font-mono text-sm font-medium">{skill.name}</p>
                <p className="text-muted-foreground text-xs">{skill.description}</p>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("pluginsTab.mcpServersTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {plugin.external_servers.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("pluginsTab.noExternalServers")}</p>
          ) : (
            plugin.external_servers.map((server) => {
              const state = install?.servers[server.slug]
              return (
                <div key={server.slug} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-mono text-sm font-medium">{server.slug}</p>
                    {state && (
                      <Badge variant={state.status === "error" ? "destructive" : "secondary"}>
                        {state.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground font-mono text-xs break-all">{server.url}</p>
                  {state?.status === "error" && state.last_error && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertTitle>{t("pluginsTab.connectionErrorTitle")}</AlertTitle>
                      <AlertDescription>{state.last_error}</AlertDescription>
                    </Alert>
                  )}
                </div>
              )
            })
          )}
        </CardContent>
      </Card>

      {showConfig && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("pluginsTab.configTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {plugin.external_servers
              .filter((server) => server.config.length > 0)
              .map((server) => (
                <div key={server.slug} className="space-y-2">
                  {plugin.external_servers.length > 1 && (
                    <p className="text-muted-foreground font-mono text-xs font-medium">
                      {server.slug}
                    </p>
                  )}
                  {server.config.map((field) => {
                    const state = install?.servers[server.slug]
                    const isSet = state ? field.key in state.set_fields : false
                    const last4 = state?.set_fields[field.key]
                    const fieldId = `${plugin.slug}-${server.slug}-${field.key}`
                    return (
                      <div key={field.key} className="space-y-1">
                        <Label htmlFor={fieldId}>
                          {field.label}
                          {field.required && !isSet && (
                            <span className="text-destructive"> *</span>
                          )}
                          {isSet && field.secret && (
                            <span className="text-muted-foreground font-normal">
                              {" "}
                              (••••{last4})
                            </span>
                          )}
                        </Label>
                        <Input
                          id={fieldId}
                          type={field.secret ? "password" : "text"}
                          autoComplete="off"
                          value={values[server.slug]?.[field.key] ?? ""}
                          onChange={(event) =>
                            setFieldValue(server.slug, field.key, event.target.value)
                          }
                          placeholder={
                            field.secret && isSet
                              ? t("pluginsTab.secretFieldPlaceholder")
                              : undefined
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              ))}
            <Button type="button" size="sm" onClick={saveConfig} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("pluginsTab.saveConfigButton")}
            </Button>
          </CardContent>
        </Card>
      )}

      {ExtraPanel && enabled && !removed && <ExtraPanel agentId={agentId} />}
    </div>
  )
}

function OAuthCard({
  agentId,
  plugin,
  install,
  onChange,
}: {
  agentId: string
  plugin: Plugin
  install: AgentPlugin | undefined
  onChange: () => Promise<void>
}) {
  const t = useT()
  const state = install?.oauth
  const available = plugin.oauth?.available ?? false
  const revoked = state?.status === "revoked"
  const [busy, setBusy] = React.useState(false)
  const unavailableHint = t("pluginsTab.oauth.unavailableHint")

  async function connect() {
    setBusy(true)
    try {
      await beginPluginOAuth(agentId, plugin.slug) // navigates away on success
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : t("pluginsTab.oauth.toasts.connectFailed"),
      )
      setBusy(false)
    }
  }

  async function disconnect() {
    const who = state?.account_email ?? t("pluginsTab.oauth.accountFallback")
    if (!confirm(t("pluginsTab.oauth.disconnectConfirm", { who }))) return
    setBusy(true)
    try {
      await api.disconnectPluginOAuth(agentId, plugin.slug)
      toast.success(t("pluginsTab.oauth.toasts.disconnected"))
      await onChange()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("common.disconnectFailed"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("pluginsTab.oauth.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!available && !state?.connected ? (
          <>
            <Button type="button" size="sm" disabled>
              {t("pluginsTab.oauth.connectButton", { name: plugin.name })}
            </Button>
            <p className="text-muted-foreground text-xs">{unavailableHint}</p>
          </>
        ) : state?.connected ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {t("common.connectedAs")} <span className="font-medium">{state.account_email}</span>
              {revoked && <Badge variant="warning">{t("pluginsTab.oauth.reconnectBadge")}</Badge>}
            </div>
            {revoked && state.last_error && (
              <Alert variant="destructive">
                <AlertTitle>{t("pluginsTab.oauth.lostTitle")}</AlertTitle>
                <AlertDescription>{state.last_error}</AlertDescription>
              </Alert>
            )}
            {!available && <p className="text-muted-foreground text-xs">{unavailableHint}</p>}
            <div className="flex gap-2">
              {revoked && (
                <Button type="button" size="sm" onClick={connect} disabled={busy || !available}>
                  {t("pluginsTab.oauth.reconnectButton")}
                </Button>
              )}
              <Button type="button" size="sm" variant="outline" onClick={disconnect} disabled={busy}>
                {t("common.disconnect")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">{t("pluginsTab.oauth.connectPrompt")}</p>
            <Button type="button" size="sm" onClick={connect} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("pluginsTab.oauth.connectButton", { name: plugin.name })}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
